/**
 * Number Line AST Nodes
 *
 * Block-level layout nodes for number line structure:
 * NumberLineBlock → NumberLinePosition[] → InlineNode[]
 */

import { z } from 'zod';
import { InlineNodeSchema } from './inline';
import { AST_TYPES } from '../constants';
import { NUMBER_LINE_STYLES, NumberLineStyle, NUMBER_LINE_LABELS, NumberLineLabels, NUMBER_LINE_HIGHLIGHT_COLORS, NumberLineHighlightColor, NUMBER_LINE_DRAG_MODES, NumberLineDragMode } from '../constants/number-line-constants';

/**
 * A single position marker on the number line.
 * Contains at least one inline element (text, input, etc.).
 *
 * @example
 * {
 *   type: "NumberLinePosition",
 *   value: 20,
 *   inlines: [{ type: "InlineInput", name: "a", digits: 3 }]
 * }
 */
export const NumberLinePositionSchema = z.object({
  type: z.literal(AST_TYPES.NUMBER_LINE_POSITION),
  value: z.number().finite({ message: 'POSITION: value must be a finite number' }),
  inlines: z.array(InlineNodeSchema).min(1)
});

export type NumberLinePosition = z.infer<typeof NumberLinePositionSchema>;

export function createNumberLinePosition(
  value: number,
  inlines: z.infer<typeof InlineNodeSchema>[]
): NumberLinePosition {
  // Internal node rule: empty or whitespace-only content is not allowed
  const hasRealContent = inlines.some((n) => n.type !== AST_TYPES.SPACE);
  if (!hasRealContent) {
    throw new Error(`POSITION[value=${value}]: content cannot be empty or whitespace only`);
  }

  // Internal node rule: max one input widget
  const inputs = inlines.filter((i) => i.type === AST_TYPES.INLINE_INPUT);
  if (inputs.length > 1) {
    throw new Error(
      `POSITION[value=${value}]: only one input widget is allowed, got ${inputs.length}`
    );
  }

  return NumberLinePositionSchema.parse({
    type: AST_TYPES.NUMBER_LINE_POSITION,
    value,
    inlines
  });
}

export function isNumberLinePosition(value: unknown): value is NumberLinePosition {
  return NumberLinePositionSchema.safeParse(value).success;
}

// ═══════════════════════════════════════════════════════════════════════════
// Number Line Counter
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Counter node — dynamic subdivision control embedded in a number line.
 * The number line step is recomputed as: (max - min) / counter_value.
 *
 * Invariant: min >= 1 (a subdivision of 0 is meaningless).
 * The `default` fallback lives only in createNumberLineCounter, not here,
 * to avoid Zod transform side-effects on the parsed shape.
 */
export const NumberLineCounterSchema = z.object({
  type: z.literal(AST_TYPES.NUMBER_LINE_COUNTER),
  min: z.number().int().min(1, { message: 'COUNTER: min must be >= 1' }),
  max: z.number().int().positive({ message: 'COUNTER: max must be > 0' }),
  default: z.number().int().nonnegative().optional(),
  name: z.string().optional(),
  answer: z.number().int().positive({ message: 'COUNTER: answer must be a positive integer' }).optional(),
}).refine((d) => d.min <= d.max, {
  message: 'COUNTER: min must be <= max',
});

export type NumberLineCounter = z.infer<typeof NumberLineCounterSchema>;

export function createNumberLineCounter(
  max: number,
  min?: number,
  defaultValue?: number,
  name?: string,
  answer?: number
): NumberLineCounter {
  return NumberLineCounterSchema.parse({
    type: AST_TYPES.NUMBER_LINE_COUNTER,
    min: min ?? 1,
    max,
    ...(defaultValue !== undefined && { default: defaultValue }),
    ...(name !== undefined && { name }),
    ...(answer !== undefined && { answer }),
  });
}

export function isNumberLineCounter(value: unknown): value is NumberLineCounter {
  return NumberLineCounterSchema.safeParse(value).success;
}

// ═══════════════════════════════════════════════════════════════════════════
// Number Line Highlight
// ═══════════════════════════════════════════════════════════════════════════

/**
 * A highlighted range on the number line axis.
 *
 * `color` is constrained to the semantic set in NUMBER_LINE_HIGHLIGHT_COLORS
 * so renderers can safely map values to design tokens.
 */
export const NumberLineHighlightSchema = z.object({
  type: z.literal(AST_TYPES.NUMBER_LINE_HIGHLIGHT),
  from: z.number().finite({ message: 'HIGHLIGHT: from must be a finite number' }),
  to: z.number().finite({ message: 'HIGHLIGHT: to must be a finite number' }),
  color: z.enum(NUMBER_LINE_HIGHLIGHT_COLORS, {
    errorMap: () => ({
      message: `HIGHLIGHT: color must be one of ${NUMBER_LINE_HIGHLIGHT_COLORS.join(', ')}`
    })
  }).optional(),
  label: z.string().optional(),
}).refine((d) => d.from < d.to, {
  message: 'HIGHLIGHT: from must be less than to',
});

export type NumberLineHighlight = z.infer<typeof NumberLineHighlightSchema>;

export function createNumberLineHighlight(
  from: number,
  to: number,
  color?: NumberLineHighlightColor,
  label?: string
): NumberLineHighlight {
  return NumberLineHighlightSchema.parse({
    type: AST_TYPES.NUMBER_LINE_HIGHLIGHT,
    from,
    to,
    ...(color !== undefined && { color }),
    ...(label !== undefined && { label }),
  });
}

export function isNumberLineHighlight(value: unknown): value is NumberLineHighlight {
  return NumberLineHighlightSchema.safeParse(value).success;
}

// ═══════════════════════════════════════════════════════════════════════════
// Number Line cursor
// ═══════════════════════════════════════════════════════════════════════════

export const NumberLineCursorSchema = z.object({
  type: z.literal(AST_TYPES.NUMBER_LINE_CURSOR),
  value: z.number().finite({ message: 'CURSOR: value must be a finite number' }).default(0),
  answer: z.number({required_error: 'CURSOR: answer is required'}).finite({ message: 'CURSOR: answer must be a finite number' }),
  color: z.string().regex(/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/, {
    message: 'CURSOR: color must be a hex value (e.g. "#3B82F6" or "#F00")'
  }).optional(),
  fillRange: z.boolean().default(false),
  label: z.string().optional(),
});

export type NumberLineCursor = z.infer<typeof NumberLineCursorSchema>;

export function createNumberLineCursor(
  answer: number,
  value?: number,
  color?: string,
  fillRange?: boolean,
  label?: string
): NumberLineCursor {
  return NumberLineCursorSchema.parse({
    type: AST_TYPES.NUMBER_LINE_CURSOR,
    answer,
    ...(value !== undefined && { value }),
    ...(color !== undefined && { color }),
    ...(fillRange !== undefined && { fillRange }),
    ...(label !== undefined && { label }),
  });
}

export function isNumberLineCursor(value: unknown): value is NumberLineCursor {
  return NumberLineCursorSchema.safeParse(value).success;
}
// Number Line Arrow
// ═══════════════════════════════════════════════════════════════════════════

/**
 * A left-to-right arrow annotation above a number line interval.
 *
 * Optional content is inline so authors can write labels such as `+1`, `+ 10`, or richer
 * inline elements that already exist in the DSL.
 */
export const NumberLineArrowSchema = z.object({
  type: z.literal(AST_TYPES.NUMBER_LINE_ARROW),
  from: z.number().finite({ message: 'ARROW: from must be a finite number' }),
  to: z.number().finite({ message: 'ARROW: to must be a finite number' }),
  color: z.string().optional(),
  inlines: z.array(InlineNodeSchema),
}).refine((d) => d.from < d.to, {
  message: 'ARROW: from must be less than to',
});

export type NumberLineArrow = z.infer<typeof NumberLineArrowSchema>;

export function createNumberLineArrow(
  from: number,
  to: number,
  inlines: z.infer<typeof InlineNodeSchema>[],
  color?: string,
): NumberLineArrow {
  return NumberLineArrowSchema.parse({
    type: AST_TYPES.NUMBER_LINE_ARROW,
    from,
    to,
    ...(color !== undefined && { color }),
    inlines,
  });
}

export function isNumberLineArrow(value: unknown): value is NumberLineArrow {
  return NumberLineArrowSchema.safeParse(value).success;
}

/**
 * Number line block schema — the root node for a number line exercise element.
 *
 * @example
 * {
 *   type: "NumberLineBlock",
 *   min: 0, max: 100, step: 10, minorTicks: 0, style: "arrow-right",
 *   positions: [
 *     { type: "NumberLinePosition", value: 20, inlines: [...] }
 *   ]
 * }
 */
export const NumberLineBlockSchema = z.object({
  type: z.literal(AST_TYPES.NUMBER_LINE_BLOCK),
  min: z.number().finite({ message: 'NUMBER_LINE: min must be a finite number' }),
  max: z.number().finite({ message: 'NUMBER_LINE: max must be a finite number' }),
  step: z.number()
  .positive({ message: 'NUMBER_LINE: step must be greater than 0' })
  .default(1),
  minorTicks: z.number().int().nonnegative({ message: 'NUMBER_LINE: minorTicks must be >= 0' }).optional(),
  style: z.enum(NUMBER_LINE_STYLES, {
  errorMap: () => ({
    message: `NUMBER_LINE: style must be one of ${NUMBER_LINE_STYLES.join(', ')}`
  })
}).optional(),
  labelVisibility: z.enum(NUMBER_LINE_LABELS, {
  errorMap: () => ({
    message: `NUMBER_LINE: labelVisibility must be one of ${NUMBER_LINE_LABELS.join(', ')}`
  })
}).optional(),
  counter: NumberLineCounterSchema.optional(),
  highlights: z.array(NumberLineHighlightSchema).optional(),
  cursors: z.array(NumberLineCursorSchema).optional(),
  arrows: z.array(NumberLineArrowSchema).optional(),
  dragMode: z.enum(NUMBER_LINE_DRAG_MODES, {
  errorMap: () => ({
    message: `NUMBER_LINE: dragMode must be one of ${NUMBER_LINE_DRAG_MODES.join(', ')}`
  })
}).optional(),
  positions: z.array(NumberLinePositionSchema)
}).refine((data) => data.min < data.max, {
  message: 'NUMBER_LINE: min must be less than max'
}).superRefine((data, ctx) => {
  const seen = new Set<number>();
  for (const pos of data.positions) {
    if (pos.value < data.min || pos.value > data.max) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `NUMBER_LINE: POSITION value (${pos.value}) must be within [${data.min}, ${data.max}]`
      });
    }
    if (seen.has(pos.value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `NUMBER_LINE: duplicate POSITION value (${pos.value})`
      });
    }
    seen.add(pos.value);
  }
  // Validate highlights are within [min, max]
  for (const h of data.highlights ?? []) {
    if (h.from < data.min || h.to > data.max) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `NUMBER_LINE: HIGHLIGHT range [${h.from}, ${h.to}] must be within [${data.min}, ${data.max}]`
      });
    }
  }
  // Validate arrows are within [min, max]
  for (const arrow of data.arrows ?? []) {
    if (arrow.from < data.min || arrow.to > data.max) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `NUMBER_LINE: ARROW range [${arrow.from}, ${arrow.to}] must be within [${data.min}, ${data.max}]`
      });
    }
  }
  // Validate counter default is within counter's own [min, max]
  if (data.counter?.default !== undefined) {
    if (data.counter.default < data.counter.min || data.counter.default > data.counter.max) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `NUMBER_LINE: COUNTER default (${data.counter.default}) must be within [${data.counter.min}, ${data.counter.max}]`
      });
    }
  }
  // Validate cursor value and answer are within [min, max]
  for (const c of data.cursors ?? []) {
    if (c.value < data.min || c.value > data.max) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `NUMBER_LINE: CURSOR value (${c.value}) must be within [${data.min}, ${data.max}]`
      });
    }
    if (c.answer < data.min || c.answer > data.max) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `NUMBER_LINE: CURSOR answer (${c.answer}) must be within [${data.min}, ${data.max}]`
      });
    }
  }
});

export type NumberLineBlock = z.infer<typeof NumberLineBlockSchema>;

export function createNumberLineBlock(
  min: number,
  max: number,
  positions: NumberLinePosition[],
  step?: number,
  minorTicks?: number,
  style?: NumberLineStyle,
  labelVisibility?: NumberLineLabels,
  counter?: NumberLineCounter,
  highlights?: NumberLineHighlight[],
  cursors?: NumberLineCursor[],
  arrows?: NumberLineArrow[],
  dragMode?: NumberLineDragMode
): NumberLineBlock {
  return NumberLineBlockSchema.parse({
    type: AST_TYPES.NUMBER_LINE_BLOCK,
    min,
    max,
    step,
    minorTicks,
    style,
    labelVisibility,
    counter,
    highlights,
    cursors,
    arrows,
    dragMode,
    positions
  });
}

export function isNumberLineBlock(value: unknown): value is NumberLineBlock {
  return NumberLineBlockSchema.safeParse(value).success;
}
